import { useMemo, useRef, useState, useId, useLayoutEffect, ReactNode } from "react";
import { Field } from "react-migration/components/Field/Field";
import { addBufferToGeometry } from "react-migration/domains/sites/helpers/addBufferToGeometry";
import { MeasurementSystem, userStore } from "src/js/stores/user/store";
import { useRadiusBuffer } from "react-migration/lib/map/useRadiusBuffer";
import { SelectionGeometry } from "src/js/stores/map/store";
import { Feature, Properties } from "@turf/turf";
import { Color } from "deck.gl";
import { convertLengthToAllUnits, LengthUnit } from "../util/measurements";
import { useSnapshot } from "valtio";

const DEFAULT_RADIUS_COLOR: Color = [255, 255, 255, 255];

export interface RadiusInputRenderProps {
  value: string;
  numericValue: number;
  valueInMeters: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange: (value: number) => void;
  onBlur: () => void;
  min: number;
  max: number;
  step: number;
  unit: string;
  isImperial: boolean;
  id: string;
  /** Unique ID for the input */
}

export function useRadiusInput({
  geometry,
  enabled,
  defaultRadius = 100,
  defaultStep = 0.1,
  radiusLimit = 2000,
  radiusColor = DEFAULT_RADIUS_COLOR,
  onRadiusChange,
  renderInput,
}: {
  geometry: SelectionGeometry | null;
  enabled: boolean;
  defaultRadius?: number;
  defaultStep?: number;
  radiusLimit?: number;
  radiusColor?: Color;
  onRadiusChange?: (radius: number | undefined) => void;
  renderInput?: (props: RadiusInputRenderProps) => ReactNode;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useSnapshot(userStore);
  const isImperial = user.settings.unit === MeasurementSystem.IMPERIAL;
  const lengthUnit = useMemo(() => (isImperial ? "miles" : "km"), [isImperial]);
  const [inputValue, setInputValue] = useState<string>(
    String(convertLengthToAllUnits(defaultRadius, "meters")[lengthUnit])
  );
  const [lastValidInput, setLastValidInput] = useState<number>(Number(inputValue));

  // Handle toggling unit type
  const firstUpdate = useRef(true);
  useLayoutEffect(() => {
    // don't run on first render
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    // convert last valid input to the new unit type using the previous length unit
    const currentMeters = convertLengthToAllUnits(lastValidInput, isImperial ? "km" : "miles").meters;
    let newValue = convertLengthToAllUnits(currentMeters, "meters")[lengthUnit];
    // ensure newValue does not exceed radiusLimit
    const limit = convertLengthToAllUnits(radiusLimit, "meters")[lengthUnit];
    if (newValue > limit) {
      newValue = limit;
    }

    setInputValue(String(newValue));
    setLastValidInput(newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lengthUnit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { display, value } = parseAndValidateLengthInput({
      val: e.target.value,
      radiusLimit,
      unit: lengthUnit,
    });
    setInputValue(display ?? inputValue);
    value !== undefined && setLastValidInput(value);
    onRadiusChange?.(value);
  };

  const handleValueChange = (value: number) => {
    const { display, value: validatedValue } = parseAndValidateLengthInput({
      val: String(value),
      radiusLimit,
      unit: lengthUnit,
    });
    setInputValue(display ?? inputValue);
    validatedValue !== undefined && setLastValidInput(validatedValue);
    onRadiusChange?.(validatedValue);
  };

  const handleBlur = () => {
    if (inputValue === "") {
      setInputValue("0");
    }
    if (inputValue.startsWith(".")) {
      setInputValue("0" + inputValue);
    }
  };

  const handleGroupClick = () => {
    inputRef.current?.focus();
  };

  useRadiusBuffer({
    geometry,
    bufferMeters: convertLengthToAllUnits(lastValidInput, lengthUnit).meters,
    enabled,
    color: radiusColor,
  });

  const bufferedGeometry = useMemo(() => {
    if (!geometry) return;
    return addBufferToGeometry(geometry, convertLengthToAllUnits(lastValidInput, lengthUnit).meters);
  }, [geometry, lastValidInput, lengthUnit]);

  const currentRadiusInMeters = convertLengthToAllUnits(lastValidInput, lengthUnit).meters;
  const maxInDisplayUnits = convertLengthToAllUnits(radiusLimit, "meters")[lengthUnit];

  const renderProps: RadiusInputRenderProps = {
    value: inputValue,
    numericValue: lastValidInput,
    valueInMeters: currentRadiusInMeters,
    onChange: handleInputChange,
    onValueChange: handleValueChange,
    onBlur: handleBlur,
    min: 0,
    max: maxInDisplayUnits,
    step: defaultStep,
    unit: lengthUnit,
    isImperial,
    id: `${id}-radius-input`,
  };

  const RadiusInput = renderInput ? (
    renderInput(renderProps)
  ) : (
    <DefaultRadiusInput renderProps={renderProps} inputRef={inputRef} onGroupClick={handleGroupClick} />
  );

  return {
    RadiusInput,
    bufferedGeometry: bufferedGeometry as Feature<SelectionGeometry, Properties>,
    currentRadiusInMeters,
    /** Exposed render props for advanced use cases */
    radiusInputProps: renderProps,
  };
}

function DefaultRadiusInput({
  renderProps,
  inputRef,
  onGroupClick,
}: {
  renderProps: RadiusInputRenderProps;
  inputRef: React.RefObject<HTMLInputElement>;
  onGroupClick: () => void;
}) {
  const suffixOffset = useMemo(() => {
    const APPROX_CHAR_WIDTH = 9;
    const INPUT_PREFIX_OFFSET = 35;

    const onesInValue = renderProps.numericValue
      .toString()
      .split("")
      .filter((char) => char === "1").length;
    const totalNumbers = renderProps.numericValue.toString().replace(".", "").length;

    const numbersCount = onesInValue * 0.7 + totalNumbers - onesInValue || 1;
    return numbersCount * APPROX_CHAR_WIDTH + INPUT_PREFIX_OFFSET;
  }, [renderProps.numericValue]);

  return (
    <Field.Label text="Nearby buffer" className="w-full">
      <Field.Group className="relative w-full bg-light" onClick={onGroupClick}>
        <Field.Prefix className="border-r-0! pr-0">
          <i className="icon icon-lt-record-circle-line" />
        </Field.Prefix>
        <Field.Input
          id={renderProps.id}
          key={renderProps.id}
          ref={inputRef}
          placeholder="1.00"
          className="min-w-[100px] w-fit z-20 grow-0"
          value={renderProps.value}
          onChange={renderProps.onChange}
          onBlur={renderProps.onBlur}
          pattern="d\+\.\d\d$"
          step={renderProps.step}
          min={renderProps.min}
          max={renderProps.max}
          type="number"
          inputMode="decimal"
        />
        <Field.Suffix className={"border-l-0 pl-0 z-10 absolute"} style={{ left: `${suffixOffset}px` }}>
          {renderProps.unit}
        </Field.Suffix>
      </Field.Group>
    </Field.Label>
  );
}

export const parseAndValidateLengthInput = ({
  val,
  radiusLimit,
  unit,
}: {
  val: string;
  radiusLimit: number;
  unit: LengthUnit;
}) => {
  let v = val;

  // Strip leading 0s when not 0
  if (v) {
    v = v.replace(/^0+(?=\d)/, "");
  }

  // If empty input, keep the input clear but set the query to 0
  if (v === "") {
    return { display: "", value: 0 };
  }

  // If more than 2 decimal places, reset to the previous value
  if (!/^\d+(\.\d{0,2})?$/.test(v.startsWith(".") ? "0" + v : v)) {
    return { display: undefined, value: undefined };
  }

  const num = Number(v);
  const limit = convertLengthToAllUnits(radiusLimit, "meters")[unit];

  // If value is negative, reset to 0
  if (num < 0) {
    return { display: "0", value: 0 };
  }

  // If value exceeds radius limit, set to limit
  if (num > limit) {
    return { display: String(limit), value: limit };
  }

  return { display: v, value: num };
};
